import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  AppliesWhen,
  ComponentKind,
  ComponentMethod,
  componentsApi,
  inr,
  KIND_LABELS,
  METHOD_LABELS,
  Rounding,
  SalaryComponent,
} from '../../payroll.api';
import CrudTable from './CrudTable';
import { isBadNumber, num, str } from './fields';

interface Draft {
  code: string;
  name: string;
  kind: ComponentKind;
  method: ComponentMethod;
  percent: string;
  baseCode: string;
  capAmount: string;
  thresholdAmount: string;
  fixedAmount: string;
  prorate: boolean;
  rounding: Rounding;
  appliesWhen: AppliesWhen;
  sortOrder: string;
  active: boolean;
  showOnPayslip: boolean;
}

/** Which inputs each method reads. */
const USES: Record<ComponentMethod, { percent?: boolean; base?: boolean; cap?: boolean; threshold?: boolean; fixed?: boolean }> = {
  PCT_OF_GROSS: { percent: true },
  PCT_OF_COMPONENT: { percent: true, base: true, cap: true },
  BALANCE: {},
  FIXED: { fixed: true },
  PCT_OF_EARNINGS: { percent: true, threshold: true },
  PT_SLAB: {},
  EMPLOYEE_TDS: {},
  ADVANCE: {},
};

const APPLIES_LABELS: Record<AppliesWhen, string> = {
  ALWAYS: 'Everyone',
  PF: 'Employees with PF',
  ESI: 'Employees with ESI',
  PT: 'Employees with PT',
};

function describe(c: SalaryComponent): string {
  switch (c.method) {
    case 'PCT_OF_GROSS':
      return `${c.percent ?? 0}% of gross`;
    case 'PCT_OF_COMPONENT':
      return `${c.percent ?? 0}% of ${c.baseCode ?? '?'}${c.capAmount ? ` (capped at ${inr(c.capAmount)})` : ''}`;
    case 'PCT_OF_EARNINGS':
      return `${c.percent ?? 0}% of earnings${c.thresholdAmount ? `, if gross ≤ ${inr(c.thresholdAmount)}` : ''}`;
    case 'FIXED':
      return inr(c.fixedAmount);
    default:
      return METHOD_LABELS[c.method];
  }
}

/** Salary components: the structure (earnings), the deductions and the employer's share. */
export default function ComponentsTab({ canEdit }: { canEdit: boolean }) {
  return (
    <Stack gap={1}>
      <Typography variant='body2' color='text.secondary'>
        Worked out in order. Earnings marked “prorated” are paid for payable days only; Balance makes the earnings add up to the gross.
      </Typography>
      <CrudTable<SalaryComponent, Draft>
        entityName='Component'
        client={componentsApi}
        canEdit={canEdit}
        sort={(a, b) => a.sortOrder - b.sortOrder}
        columns={[
          { label: 'Order', align: 'center', render: (c) => c.sortOrder },
          {
            label: 'Component',
            render: (c) => (
              <>
                <b>{c.name}</b> <Typography component='span' variant='caption' color='text.secondary'>({c.code})</Typography>
              </>
            ),
          },
          {
            label: 'Type',
            render: (c) => (
              <Chip
                size='small'
                color={c.kind === 'EARNING' ? 'success' : c.kind === 'DEDUCTION' ? 'error' : 'default'}
                label={KIND_LABELS[c.kind]}
              />
            ),
          },
          { label: 'Rule', render: describe },
          { label: 'Applies to', render: (c) => APPLIES_LABELS[c.appliesWhen] },
          { label: 'Prorated', align: 'center', render: (c) => (c.prorate ? 'Yes' : 'No') },
          { label: 'Rounding', align: 'center', render: (c) => c.rounding },
          { label: 'Active', align: 'center', render: (c) => <Chip size='small' color={c.active ? 'success' : 'default'} label={c.active ? 'Yes' : 'No'} /> },
        ]}
        emptyDraft={() => ({
          code: '',
          name: '',
          kind: 'EARNING',
          method: 'PCT_OF_GROSS',
          percent: '',
          baseCode: '',
          capAmount: '',
          thresholdAmount: '',
          fixedAmount: '',
          prorate: true,
          rounding: 'NONE',
          appliesWhen: 'ALWAYS',
          sortOrder: '100',
          active: true,
          showOnPayslip: true,
        })}
        toDraft={(c) => ({
          code: c.code,
          name: c.name,
          kind: c.kind,
          method: c.method,
          percent: str(c.percent),
          baseCode: c.baseCode ?? '',
          capAmount: str(c.capAmount),
          thresholdAmount: str(c.thresholdAmount),
          fixedAmount: str(c.fixedAmount),
          prorate: c.prorate,
          rounding: c.rounding,
          appliesWhen: c.appliesWhen,
          sortOrder: str(c.sortOrder),
          active: c.active,
          showOnPayslip: c.showOnPayslip,
        })}
        validate={(d) => {
          if (!d.code.trim() || !d.name.trim()) return 'Code and name are required.';
          const u = USES[d.method];
          if (u.percent && d.percent.trim() === '') return 'Enter the percent.';
          if (u.base && !d.baseCode.trim()) return 'Enter the code of the component it is a percent of.';
          if (u.fixed && d.fixedAmount.trim() === '') return 'Enter the amount.';
          if ([d.percent, d.capAmount, d.thresholdAmount, d.fixedAmount, d.sortOrder].some(isBadNumber)) return 'Amounts must be numbers.';
          return null;
        }}
        toPayload={(d) => {
          const u = USES[d.method];
          return {
            code: d.code.trim().toUpperCase(),
            name: d.name.trim(),
            kind: d.kind,
            method: d.method,
            percent: u.percent ? num(d.percent) : null,
            baseCode: u.base ? d.baseCode.trim().toUpperCase() : null,
            capAmount: u.cap ? num(d.capAmount) : null,
            thresholdAmount: u.threshold ? num(d.thresholdAmount) : null,
            fixedAmount: u.fixed ? num(d.fixedAmount) : null,
            prorate: d.prorate,
            rounding: d.rounding,
            appliesWhen: d.appliesWhen,
            sortOrder: num(d.sortOrder) ?? 0,
            active: d.active,
            showOnPayslip: d.showOnPayslip,
          };
        }}
        renderForm={(d, set) => {
          const u = USES[d.method];
          return (
            <>
              <Stack direction='row' gap={2}>
                <TextField size='small' label='Code' value={d.code} onChange={(e) => set({ code: e.target.value })} sx={{ width: 160 }} helperText='e.g. BASIC, HRA, PF_EE' />
                <TextField size='small' label='Name' fullWidth value={d.name} onChange={(e) => set({ name: e.target.value })} />
              </Stack>
              <Stack direction='row' gap={2}>
                <TextField select size='small' label='Type' fullWidth value={d.kind} onChange={(e) => set({ kind: e.target.value as ComponentKind })}>
                  {Object.entries(KIND_LABELS).map(([k, l]) => (
                    <MenuItem key={k} value={k}>
                      {l}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField size='small' label='Order' type='number' value={d.sortOrder} onChange={(e) => set({ sortOrder: e.target.value })} sx={{ width: 120 }} />
              </Stack>
              <TextField select size='small' label='Calculated as' value={d.method} onChange={(e) => set({ method: e.target.value as ComponentMethod })}>
                {Object.entries(METHOD_LABELS).map(([k, l]) => (
                  <MenuItem key={k} value={k}>
                    {l}
                  </MenuItem>
                ))}
              </TextField>
              {(u.percent || u.base) && (
                <Stack direction='row' gap={2}>
                  {u.percent && (
                    <TextField size='small' label='Percent' type='number' inputProps={{ step: 'any' }} value={d.percent} onChange={(e) => set({ percent: e.target.value })} fullWidth />
                  )}
                  {u.base && (
                    <TextField size='small' label='Of component (code)' value={d.baseCode} onChange={(e) => set({ baseCode: e.target.value })} fullWidth />
                  )}
                </Stack>
              )}
              {u.cap && (
                <TextField
                  size='small'
                  label='Cap (wage ceiling)'
                  type='number'
                  value={d.capAmount}
                  onChange={(e) => set({ capAmount: e.target.value })}
                  helperText='The base is limited to this amount before the percent is applied, e.g. 15000 for PF. Blank = no cap.'
                />
              )}
              {u.threshold && (
                <TextField
                  size='small'
                  label='Only if monthly gross is at most'
                  type='number'
                  value={d.thresholdAmount}
                  onChange={(e) => set({ thresholdAmount: e.target.value })}
                  helperText='e.g. 21000 for ESI. Blank = always.'
                />
              )}
              {u.fixed && (
                <TextField size='small' label='Amount per month' type='number' value={d.fixedAmount} onChange={(e) => set({ fixedAmount: e.target.value })} />
              )}
              <Stack direction='row' gap={2}>
                <TextField select size='small' label='Applies to' fullWidth value={d.appliesWhen} onChange={(e) => set({ appliesWhen: e.target.value as AppliesWhen })}>
                  {Object.entries(APPLIES_LABELS).map(([k, l]) => (
                    <MenuItem key={k} value={k}>
                      {l}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField select size='small' label='Rounding' fullWidth value={d.rounding} onChange={(e) => set({ rounding: e.target.value as Rounding })}>
                  <MenuItem value='NONE'>None (paise)</MenuItem>
                  <MenuItem value='NEAREST'>Nearest rupee</MenuItem>
                  <MenuItem value='UP'>Up to the rupee</MenuItem>
                </TextField>
              </Stack>
              <Stack direction='row' flexWrap='wrap'>
                <FormControlLabel control={<Switch checked={d.prorate} onChange={(e) => set({ prorate: e.target.checked })} />} label='Prorate by payable days' />
                <FormControlLabel control={<Switch checked={d.showOnPayslip} onChange={(e) => set({ showOnPayslip: e.target.checked })} />} label='Show on payslip' />
                <FormControlLabel control={<Switch checked={d.active} onChange={(e) => set({ active: e.target.checked })} />} label='Active' />
              </Stack>
            </>
          );
        }}
      />
    </Stack>
  );
}
